import {
    type CommentTagCatalogue,
    Sentiments,
    SURVEY_COMMENT_MAX_LENGTH,
    type SystemSurveyComment,
} from '@engagement-web-app/common';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/Button';
import { Dialog, DialogButtonGroup, DialogClose } from '@/components/Dialog';
import { Flex } from '@/components/Flex';
import { Form } from '@/components/Form';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { ToggleButton } from '@/components/ToggleButton';
import { sentimentLabel } from '../sentiment';
import { groupTagsByGroupId } from '../tags';
import * as styles from './index.css';

type EditCommentSubmit = {
    answer: string;
    sentiment: Sentiments;
    tagIds: string[];
};

type EditCommentDialogProps = {
    /** The comment being edited; null closes the dialog. */
    comment: SystemSurveyComment | null;
    catalogue: CommentTagCatalogue | null;
    isSubmitting: boolean;
    onOpenChange: (open: boolean) => void;
    onSubmit: (values: EditCommentSubmit) => Promise<void>;
};

/**
 * Lets a consultant correct one comment's masked text, sentiment and tags before
 * the survey results are published.
 */
export function EditCommentDialog({
    comment,
    catalogue,
    isSubmitting,
    onOpenChange,
    onSubmit,
}: EditCommentDialogProps) {
    const [answer, setAnswer] = useState('');
    const [sentiment, setSentiment] = useState<Sentiments>('NEUTRAL');
    const [tagIds, setTagIds] = useState<string[]>([]);
    const tagsByGroupId = useMemo(() => groupTagsByGroupId(catalogue), [catalogue]);

    // Re-seed the fields whenever a different comment is opened.
    useEffect(() => {
        if (!comment) return;
        setAnswer(comment.answer);
        setSentiment(comment.sentiment);
        setTagIds(comment.tags.map((tag) => tag.id));
    }, [comment]);

    const toggleTag = (tagId: string) => {
        setTagIds((current) => (current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId]));
    };

    return (
        <Dialog title="コメントを編集する" open={comment !== null} onOpenChange={onOpenChange}>
            <Flex direction="column" gap="5">
                <div>
                    <div className={styles.rawAnswerLabel}>マスキング前のコメント</div>
                    <div className={styles.rawAnswer}>{comment?.rawAnswer}</div>
                </div>

                <Form.Field
                    label="コメント"
                    control={(props) => (
                        <Textarea
                            {...props}
                            fullWidth
                            value={answer}
                            maxLength={SURVEY_COMMENT_MAX_LENGTH}
                            rows={6}
                            onChange={(e) => setAnswer(e.target.value)}
                        />
                    )}
                />

                <Form.Field
                    label="感情"
                    control={(props) => (
                        <Select
                            {...props}
                            value={sentiment}
                            onChange={(e) => setSentiment(e.target.value as Sentiments)}
                        >
                            {Sentiments.options.map((value) => (
                                <option key={value} value={value}>
                                    {sentimentLabel(value)}
                                </option>
                            ))}
                        </Select>
                    )}
                />

                <div>
                    <div className={styles.tagsLabel}>タグ</div>
                    {(catalogue?.groups ?? []).map((group) => {
                        const groupTags = tagsByGroupId.get(group.id) ?? [];
                        if (groupTags.length === 0) return null;

                        return (
                            <div key={group.id} className={styles.tagGroup}>
                                <div className={styles.tagGroupName}>{group.name}</div>
                                <Flex gap="2" wrap="wrap">
                                    {groupTags.map((tag) => (
                                        <ToggleButton
                                            key={tag.id}
                                            pressed={tagIds.includes(tag.id)}
                                            onPressedChange={() => toggleTag(tag.id)}
                                        >
                                            {tag.name}
                                        </ToggleButton>
                                    ))}
                                </Flex>
                            </div>
                        );
                    })}
                </div>
            </Flex>

            <DialogButtonGroup>
                <DialogClose>
                    <Button type="button" variant="secondary" disabled={isSubmitting}>
                        キャンセル
                    </Button>
                </DialogClose>
                <Button type="button" disabled={isSubmitting} onClick={() => onSubmit({ answer, sentiment, tagIds })}>
                    {isSubmitting ? '保存中...' : '保存する'}
                </Button>
            </DialogButtonGroup>
        </Dialog>
    );
}
